"use client";

import React from "react";
import TwoColumnCard from "../LegalTwoCol";
import ParseRichText from "components/parts/ParseRichText";
import { PageContainer, ContentContainer } from "components/layout/Layout";
import { Grid } from "components/layout/Grid";

import {
  BodyContainer,
  Paragraph,
  SectionHeader,
  SectionSubHeader,
  HeadingContainer,
  SubtitleText,
  FootnoteText,
  ListItem,
  BottomContainer,
  TextContainer
} from "./style";
import { Line } from "components/modules/FeesLegal/style";
import TextLink from "components/parts/NextLink/TextLink";
import { BackLink } from "../LegalTwoCol/style";
import PdpFooter from "components/modules/pdp-footer";
import { Heading, Scales, Body } from "components/parts/Typography";
import { buildHeaders } from "utils/legal";
import isEmpty from "lodash/isEmpty";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import type { File } from "types/legal";

function getPlatform() {
  if (!process.browser) return "desktop";
  const userAgent = navigator.userAgent;

  if (userAgent) {
    if (/android/i.test(userAgent)) return "android";
    if (/iPhone|iPod/i.test(userAgent)) return "iPhone";
  }

  return "desktop";
}
type LegalSlugProps = {
  name: string;
  lastUpdated: string;
  content?: any;
  contentPartTwo?: any;
  attachedPDFs?: File[];
  footnotes?: string;
  isDerivatives: boolean;
  artemis?: boolean;
};

const LegalSlug = ({
  lastUpdated,
  name,
  content,
  contentPartTwo,
  footnotes,
  attachedPDFs,
  isDerivatives
}: LegalSlugProps) => {
  const t = useTranslations(FILES.LEGAL);
  const tCommon = useTranslations("common");
  const config = {
    Header2: SectionHeader,
    Header3: SectionHeader,
    Header4: SectionSubHeader,
    Paragraph,
    ListItem
  };
  const headers = buildHeaders(content, contentPartTwo);

  return (
    <PageContainer>
      <ContentContainer>
        <Grid>
          <TwoColumnCard
            currentContents={headers}
            isDerivatives={isDerivatives}
          >
            <BodyContainer>
              <HeadingContainer>
                {name && (
                  <Heading
                    as="h1"
                    size="lg"
                    sm={Scales.scale6}
                    mb="8px"
                    lg={{ mt: "16px" }}
                  >
                    {name}
                  </Heading>
                )}
                {lastUpdated && (
                  <SubtitleText>
                    {t("lastUpdated")} {lastUpdated}
                  </SubtitleText>
                )}
                <Line />
              </HeadingContainer>
              <TextContainer>
                {content && <ParseRichText source={content} config={config} />}
                {contentPartTwo && (
                  <ParseRichText source={contentPartTwo} config={config} />
                )}
                {!isEmpty(attachedPDFs) &&
                  attachedPDFs?.map((attachedPDF) => (
                    <span key={attachedPDF.title}>
                      <SectionSubHeader>{attachedPDF.title}</SectionSubHeader>
                      {getPlatform() === "iPhone" ? (
                        <Body size="md">
                          <TextLink href={`https:${attachedPDF.url}`}>
                            {t("downloadText", { filename: attachedPDF.title })}
                          </TextLink>
                          .
                        </Body>
                      ) : (
                        <object
                          data={attachedPDF.url}
                          type="application/pdf"
                          width="100%"
                          height="600px"
                        >
                          <iframe
                            title={attachedPDF.title}
                            src={`https://docs.google.com/viewer?url=https:${attachedPDF.url}&embedded=true`}
                          ></iframe>
                          <Body size="md">
                            <TextLink href={`https:${attachedPDF.url}`}>
                              {t("downloadText", {
                                filename: attachedPDF.title
                              })}
                            </TextLink>
                            .
                          </Body>
                        </object>
                      )}
                    </span>
                  ))}
              </TextContainer>
              {footnotes && (
                <React.Fragment>
                  <hr />
                  <FootnoteText>{footnotes}</FootnoteText>
                </React.Fragment>
              )}
            </BodyContainer>
          </TwoColumnCard>
          <BottomContainer>
            <TextLink href={isDerivatives ? "/artemis/legal" : "/legal"}>
              <BackLink m="24px 0 !important">{t("goBackLink")}</BackLink>
            </TextLink>
          </BottomContainer>
        </Grid>
      </ContentContainer>
      <PdpFooter ctaParagraph={tCommon(`pdpFooterParagraphAccount`)} />
    </PageContainer>
  );
};

export default LegalSlug;
